import React from "react";
import { Reveal } from "./useReveal";

const OldStack = () => (
  <svg viewBox="0 0 320 240" className="w-full h-auto" aria-hidden="true">
    {/* Old / frustrating — scattered pages */}
    <rect x="10" y="30" width="110" height="150" rx="2" fill="#ECE5D6" stroke="#1A1A1A" strokeOpacity="0.2" />
    <rect x="22" y="50" width="80" height="4" fill="#1A1A1A" opacity="0.25" />
    <rect x="22" y="60" width="60" height="4" fill="#1A1A1A" opacity="0.25" />
    <rect x="22" y="70" width="75" height="4" fill="#1A1A1A" opacity="0.25" />
    <rect x="22" y="80" width="50" height="4" fill="#1A1A1A" opacity="0.25" />

    <rect x="60" y="80" width="110" height="150" rx="2" transform="rotate(6 60 80)" fill="#ECE5D6" stroke="#1A1A1A" strokeOpacity="0.2" />
    <rect x="130" y="40" width="110" height="150" rx="2" transform="rotate(-4 130 40)" fill="#ECE5D6" stroke="#1A1A1A" strokeOpacity="0.25" />
    <text x="170" y="90" fontFamily="DM Sans" fontSize="10" fontWeight="700" fill="#1A1A1A" opacity="0.5">PDF</text>
    <text x="82" y="140" fontFamily="DM Sans" fontSize="10" fontWeight="700" fill="#1A1A1A" opacity="0.5">YOUTUBE.COM</text>

    {/* Red scribble implying confusion */}
    <path
      d="M200 160 C 220 150, 230 180, 250 170 S 280 200, 300 180"
      stroke="#C0392B"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M210 190 C 230 180, 240 210, 260 195"
      stroke="#C0392B"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      opacity="0.7"
    />
  </svg>
);

const NewStack = () => (
  <svg viewBox="0 0 320 240" className="w-full h-auto" aria-hidden="true">
    {/* New — ordered winning hand */}
    {Array.from({ length: 8 }).map((_, i) => {
      const x = 20 + i * 34;
      return (
        <g key={i}>
          <rect x={x} y="40" width="28" height="160" rx="4" fill="#F5F0E8" stroke="#3D7A5A" strokeWidth="1.5" />
          <rect x={x + 1} y="38" width="28" height="160" rx="4" fill="none" stroke="#2E5C44" strokeWidth="0.8" opacity="0.6" />
          {i === 3 ? (
            <text
              x={x + 14}
              y="120"
              textAnchor="middle"
              fontFamily="Noto Serif SC, serif"
              fontSize="20"
              fontWeight="900"
              fill="#C0392B"
            >
              中
            </text>
          ) : (
            <>
              <circle cx={x + 14} cy="90" r="3" fill="#3D7A5A" />
              <circle cx={x + 14} cy="120" r="3" fill="#3D7A5A" />
              <circle cx={x + 14} cy="150" r="3" fill="#3D7A5A" />
            </>
          )}
        </g>
      );
    })}
    {/* Subtle green check arc */}
    <path
      d="M40 220 L 130 220 L 250 220"
      stroke="#3D7A5A"
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="6 6"
      opacity="0.7"
    />
  </svg>
);

const Problem = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="bg-[#F5F0E8] py-20 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-14 md:gap-20 items-center">
        {/* Left: statement */}
        <Reveal>
          <p
            data-testid="problem-kicker"
            className="text-[11px] md:text-xs uppercase tracking-[0.25em] text-[#3D7A5A] mb-5"
          >
            The Problem
          </p>
          <h2
            data-testid="problem-headline"
            className="text-3xl sm:text-4xl md:text-[44px] leading-[1.08] tracking-tight text-[#1A1A1A]"
          >
            <span className="font-black">Mahjong is having its moment.</span>{" "}
            <span className="font-light">The learning resources haven't caught up.</span>
          </h2>
          <p
            data-testid="problem-body"
            className="mt-6 text-[15px] md:text-base text-[#3a3a38] leading-relaxed max-w-lg"
          >
            Most Mahjong resources are scattered PDFs, outdated YouTube videos,
            and rule sheets that assume you already know how to play.
            MahjongTrainer is built differently.
          </p>

          <div className="mt-8 flex items-center gap-6">
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#6b6a64] mb-1">
                Before
              </div>
              <div className="font-black text-[#C0392B] text-lg">Chaotic</div>
            </div>
            <div className="w-12 h-px bg-[#1A1A1A]/20" />
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#6b6a64] mb-1">
                With MT
              </div>
              <div className="font-black text-[#3D7A5A] text-lg">Coached</div>
            </div>
          </div>
        </Reveal>

        {/* Right: illustrated contrast */}
        <Reveal delay={120}>
          <div className="grid grid-cols-2 gap-4 md:gap-6 items-stretch">
            <div
              data-testid="problem-contrast-old"
              className="bg-[#ECE5D6] border border-[#E5DFD3] p-5 rounded-sm flex flex-col"
            >
              <OldStack />
              <p className="mt-3 text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#6b6a64]">
                Before
              </p>
            </div>
            <div
              data-testid="problem-contrast-new"
              className="bg-[#F5F0E8] border border-[#3D7A5A]/40 p-5 rounded-sm flex flex-col"
            >
              <NewStack />
              <p className="mt-3 text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#3D7A5A] font-bold">
                With MahjongTrainer
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Problem;
